import React, { ReactNode, useEffect, useRef } from 'react';
import { Icon } from 'semantic-ui-react';
import { Check } from 'react-feather';
import DOMPurify from 'dompurify';
import { HelpTooltip, TooltipPopup } from 'components/tooltip';
import styles from './styles/InputWrap.module.scss';

interface ITooltipPopupConfig {
  title?: string;
  numberPlateLabel?: string;
  makeLabel?: string;
  modelLabel?: string;
  buttonText?: string;
  successMessage?: string;
  eventName?: string;
  onSubmit?: (data: { numberPlate: string; make: string; model: string }) => void;
}

interface IProps {
  className?: string;
  errorClassName?: string;
  errorLabelClassName?: string;
  children: any;
  description?: string;
  error?: boolean | ReactNode;
  hideInfoCheck?: boolean;
  info?: string;
  inputImg?: string;
  inputWrapClassName?: string;
  label?: string;
  labelAfter?: string;
  labelClassName?: string;
  name?: string;
  tooltip?: string;
  tooltipPopup?: ITooltipPopupConfig | boolean;
  rightIcon?: ReactNode;
  scrollIntoView?: boolean;
  tooltipProps?: any;
}

export const InputTooltip = ({ tooltip, on = ['click', 'hover'] as any }) => (
  <HelpTooltip
    className={styles.tooltip}
    toggleClassName={styles.toggle}
    position="top center"
    on={on}
    content={
      <div
        className={styles.toggleContent}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(tooltip, {
            ADD_ATTR: ['target', 'rel'],
          }),
        }}
      />
    }
  />
);

const InputWrap = ({
  className,
  description,
  errorClassName,
  errorLabelClassName,
  children,
  error,
  hideInfoCheck,
  info,
  inputImg,
  inputWrapClassName,
  label,
  labelAfter,
  labelClassName,
  name,
  rightIcon,
  scrollIntoView,
  tooltip,
  tooltipPopup,
  tooltipProps,
}: IProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const tooltipComponent = tooltip && (
    <InputTooltip tooltip={tooltip} {...tooltipProps} />
  );

  const tooltipPopupComponent = tooltipPopup && (
    <TooltipPopup
      {...(typeof tooltipPopup === 'object' ? tooltipPopup : {})}
      toggleClassName={styles.toggle}
    />
  );

  useEffect(() => {
    if (scrollIntoView) {
      // block: 'center' — 'nearest' can no-op on Android Chrome when the
      // field is partially in view, leaving the user with no scroll feedback.
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [scrollIntoView]);

  return (
    <div
      ref={ref}
      className={classnames(
        styles.container,
        !!error && styles.error,
        !!error && errorClassName,
        !!error && typeof error === 'string' && 'withErrorText',
        className,
      )}
    >
      {label && (
        <label
          htmlFor={name}
          className={classnames(styles.label, labelClassName)}
        >
          <span className={styles.textWrap}>
            {typeof label === 'string' ? (
              <span
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(label),
                }}
              />
            ) : (
              label
            )}
            {description && (
              <div className={styles.description}>
                {description}
                {tooltipComponent}
                {tooltipPopupComponent}
              </div>
            )}
            {!description && tooltipComponent}
            {!description && tooltipPopupComponent}
          </span>
        </label>
      )}
      <div className={styles.inputContainer}>
        <div className={classnames(styles.inputWrap, inputWrapClassName)}>
          {!!error ? (
            <Icon
              className={classnames('errorIcon', styles.errorIcon)}
              name="exclamation circle"
            />
          ) : (
            inputImg && <img className={styles.inputImg} src={inputImg} />
          )}
          {children}
          {rightIcon && <div className={styles.rightIcon}>{rightIcon}</div>}
        </div>
        {/* If error is a boolean just show red border else we render it if its a valid element */}
        {!!error &&
          (typeof error === 'string' || React.isValidElement(error)) && (
            <div
              className={classnames(
                'errorText',
                styles.errorText,
                errorLabelClassName,
              )}
            >
              {error}
            </div>
          )}
        {info && (
          <div className={styles.info}>
            {!hideInfoCheck && <Check size={18} />}
            {info}
          </div>
        )}
      </div>
      {labelAfter && (
        <div className={classnames(styles.labelAfter, labelClassName)}>
          {labelAfter}
        </div>
      )}
    </div>
  );
};

export default InputWrap;
